import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import type {
  DesiredActionKey,
  DesiredActionResponse,
  MultipleChoiceArtifact,
  ResponseArtifactDTO,
} from '@dreamai/types';
import { useTranslation } from 'react-i18next';
import api from '../../api';
import { ReplyToItem } from '../../contexts/ReplyToContext';
import { artifactCheckoutResume } from '../chat/HostedCheckoutArtifactResume';
import {
  FlightBookingSelection,
  FlightOffer,
  readFlightBookingState,
} from '../chat/sidebar/flightArtifact';
import {
  HotelBookingSelection,
  HotelOffer,
  readHotelBookingState,
} from '../chat/sidebar/hotelArtifact';
import SpendingApprovalModal, {
  CardSelection,
} from '../chat/sidebar/SpendingApprovalModal';
import FlightBookingProgressSheet from '../chat/sidebar/FlightBookingProgressSheet';
import HotelBookingProgressSheet from '../chat/sidebar/HotelBookingProgressSheet';
import HotelRateQuoteNotice from '../chat/sidebar/HotelRateQuoteNotice';
import HotelArtifactMap from '../chat/sidebar/HotelArtifactMap';
import { areDreamRailChoices } from '../chat/sidebar/railArtifact';
import {
  useFlightArtifactFetcher,
  useFlightBookingState,
} from '../chat/sidebar/useFlightBookingState';
import {
  useHotelArtifactFetcher,
  useHotelBookingState,
} from '../chat/sidebar/useHotelBookingState';
import { useHotelRateQuote } from '../chat/sidebar/useHotelRateQuote';
import {
  choiceNeedsLivePrices,
  isLivePricesRequired,
  needsLivePrices,
  refreshLivePrices,
} from '../sharedArtifactLivePrices';
import { DetailsSheet, FlightSheet } from './Details';
import { OptionCard, SkeletonCard, timeOf, ukDate } from './cards';
import {
  adaptFlightChoice,
  adaptHotelChoice,
  adaptRailChoice,
  adaptReturnChoice,
  groupOf,
  type ReturnFlight,
  type TravelOption,
} from './data';
import {
  adaptTravelChoices,
  hasStaleReturnChoices,
  shouldShowTravelSkeletons,
  sorted,
  STAY_SORTS,
  TRANSPORT_SORTS,
  TravelCard,
  TravelChoice,
  type SortKey,
} from './travelOptionsModel';
import { travelApprovalDetails } from './approvalDetails';
import { travelPageTitle } from './travelPageTitle';
import './travel-options.scss';

type TravelSelection = FlightBookingSelection | HotelBookingSelection;
type MultipleChoiceArtifactWithTripName = MultipleChoiceArtifact & {
  tripName?: string | null;
};
type MandateItem = {
  actionLine: string;
  descriptionLine: string;
  price: number;
  price_currency: string;
  isDeposit?: boolean;
};

interface TravelOptionsPageProps {
  artifact: MultipleChoiceArtifact;
  shareToken?: string;
  readOnly: boolean;
  submitting: boolean;
  agentName?: string;
  agentImageUrl?: string;
  chatButtonText: string;
  onBook: (
    selection: TravelSelection,
    desiredAction: DesiredActionResponse,
    mandateItems: MandateItem[],
    cardSelection?: CardSelection,
  ) => void | Promise<void>;
  onChatAbout?: () => void;
  onAcknowledge?: () => void;
  onTitleChange?: (title: string | null) => void;
}

interface ReturnResponse {
  success: boolean;
  sameTicket?: unknown[];
  separateTickets?: unknown[];
  refreshAt?: string;
}

interface PublicTravelArtifactResponse {
  success: boolean;
  artifact?: MultipleChoiceArtifact;
}

interface RelatedResponse {
  artifacts?: Array<{
    artifact?: MultipleChoiceArtifactWithTripName;
    shareToken?: string | null;
  }>;
}

type RelatedArtifactState = Pick<
  MultipleChoiceArtifact,
  'status' | 'userResponse'
>;

interface BookingIntent {
  card: TravelCard;
  offerId?: string;
  farePosition?: number;
  hotel?: boolean;
}

function flightOffer(choice: TravelChoice): FlightOffer | undefined {
  return choice.flightOffer as unknown as FlightOffer | undefined;
}

function hotelOffer(choice: TravelChoice): HotelOffer | undefined {
  return choice.hotelOffer as unknown as HotelOffer | undefined;
}

function prototypeOption(card: TravelCard): TravelOption | null {
  if (card.kind === 'flight') {
    return adaptFlightChoice(card.artifactId, card.choice as never);
  }
  if (card.kind === 'hotel') {
    return adaptHotelChoice(card.artifactId, card.choice as never);
  }
  return adaptRailChoice(card.artifactId, card.choice as never);
}

function desiredAction(
  key: DesiredActionKey,
  label: string,
): DesiredActionResponse {
  return { key, label };
}

function isRateLimited(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { response?: { status?: number } }).response?.status === 429
  );
}

function isUnavailableReturns(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const response = (error as { response?: { status?: number; data?: unknown } })
    .response;
  const data =
    typeof response?.data === 'object' && response.data !== null
      ? (response.data as Record<string, unknown>)
      : {};
  return response?.status === 404 && data.code === 'RETURNS_UNAVAILABLE';
}

function formatCountdown(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
}

function selectedIdsFromResponse(
  userResponse: MultipleChoiceArtifact['userResponse'],
): string[] {
  if (!userResponse || typeof userResponse !== 'object') return [];
  return Array.isArray(userResponse.selectedChoiceIds)
    ? userResponse.selectedChoiceIds
    : [];
}

const TravelOptionsPage: React.FC<TravelOptionsPageProps> = ({
  artifact,
  shareToken,
  readOnly,
  submitting,
  agentName,
  agentImageUrl,
  chatButtonText,
  onBook,
  onChatAbout,
  onTitleChange,
}) => {
  const { t } = useTranslation();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const queryChoice = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('choice') ?? params.get('pick');
  }, []);
  const [activeGroup, setActiveGroup] = useState<'transport' | 'stays'>(
    'transport',
  );
  const [sorts, setSorts] = useState<Record<'transport' | 'stays', SortKey>>({
    transport: 'recommended',
    stays: 'recommended',
  });
  const [relatedCards, setRelatedCards] = useState<TravelCard[]>([]);
  const [relatedTripName, setRelatedTripName] = useState<string | null>(null);
  const [relatedShareTokens, setRelatedShareTokens] = useState<
    Record<string, string | null>
  >({});
  const [relatedArtifactStates, setRelatedArtifactStates] = useState<
    Record<string, RelatedArtifactState>
  >({});
  const [relatedLoading, setRelatedLoading] = useState(true);
  const [selected, setSelected] = useState<TravelCard | null>(null);
  const [returnChoicesById, setReturnChoicesById] = useState(
    () => new Map<string, TravelCard>(),
  );
  const returnCards = Array.from(returnChoicesById.values());
  const savedReturnRows = [
    ...(Array.isArray(artifact.choices) ? artifact.choices : []),
    ...returnCards.map((card) => card.choice),
  ];
  const userSelectedGroup = useRef(false);
  // Related cards belong to another artifact: they are booked on that list's own page.
  const isRelatedCard = (card: TravelCard | null | undefined) =>
    card != null && card.artifactId !== artifact.id;
  const listClosed = (card: TravelCard | null | undefined) => {
    if (!card) return true;
    if (!isRelatedCard(card)) {
      return readOnly || artifact.status !== 'pending';
    }
    return (
      !relatedShareTokens[card.artifactId] ||
      relatedArtifactStates[card.artifactId]?.status !== 'pending'
    );
  };
  const cardBookingDisabled = (card: TravelCard | null | undefined) =>
    submitting || listClosed(card);
  const openRelatedList = useCallback(
    (card: TravelCard): boolean => {
      const token = relatedShareTokens[card.artifactId];
      if (card.artifactId === artifact.id || !token) return false;
      window.location.assign(
        `/artifact/${encodeURIComponent(token)}?choice=${encodeURIComponent(card.id)}`,
      );
      return true;
    },
    [artifact.id, relatedShareTokens],
  );
  const selectedChoiceIds = artifact.userResponse?.selectedChoiceIds ?? [];
  const [returnsLoading, setReturnsLoading] = useState(false);
  const [returnError, setReturnError] = useState<
    'returnsError' | 'returnsRateLimited' | null
  >(null);
  const [returnRefreshAt, setReturnRefreshAt] = useState<string | null>(null);
  const [countdownNow, setCountdownNow] = useState(() => Date.now());
  const [bookingRefreshing, setBookingRefreshing] = useState(false);
  const [bookingNotice, setBookingNotice] = useState<string | null>(null);
  const [localChoices, setLocalChoices] = useState<
    MultipleChoiceArtifact['choices'] | null
  >(null);
  const [pending, setPending] = useState<BookingIntent | null>(null);
  const [activeBooking, setActiveBooking] = useState<BookingIntent | null>(
    null,
  );
  const [bookingAttempt, setBookingAttempt] = useState(0);

  const pageArtifact = useMemo(
    () => (localChoices ? { ...artifact, choices: localChoices } : artifact),
    [artifact, localChoices],
  );
  const currentCards = useMemo(
    () => adaptTravelChoices(pageArtifact),
    [pageArtifact],
  );
  const cards = useMemo(
    () => [...currentCards, ...relatedCards],
    [currentCards, relatedCards],
  );
  const titleCards = cards.filter((card) => !card.choice.returnFor);
  const currentTripName = (artifact as MultipleChoiceArtifactWithTripName)
    .tripName;
  const tripName = currentTripName?.trim() ? currentTripName : relatedTripName;
  const computedTitle = relatedLoading
    ? null
    : travelPageTitle({
        artifactTitle: artifact.title,
        tripName,
        kinds: Array.from(new Set(titleCards.map((card) => card.kind))),
        listCount: new Set(titleCards.map((card) => card.artifactId)).size,
      });
  const reportedTitle = computedTitle === artifact.title ? null : computedTitle;

  useEffect(() => {
    onTitleChange?.(reportedTitle);
  }, [artifact.id, onTitleChange, reportedTitle]);
  const pinnedChoice =
    queryChoice && cards.some((card) => card.id === queryChoice)
      ? queryChoice
      : relatedLoading
        ? queryChoice
        : null;
  const prototypeOptions = useMemo(
    () =>
      cards
        .map((card) => ({ card, option: prototypeOption(card) }))
        .filter(
          (entry): entry is { card: TravelCard; option: TravelOption } =>
            entry.option !== null,
        ),
    [cards],
  );
  const currentPrototypeOptions = useMemo(() => {
    const currentCardIds = new Set(
      currentCards.map((card) => `${card.artifactId}-${card.id}`),
    );
    return prototypeOptions.filter(({ card }) =>
      currentCardIds.has(`${card.artifactId}-${card.id}`),
    );
  }, [currentCards, prototypeOptions]);
  const pinnedOption =
    prototypeOptions.find(({ card }) => card.id === pinnedChoice)?.option ??
    null;
  const pinnedCard = cards.find((card) => card.id === pinnedChoice);
  const groupedOptions = {
    transport: prototypeOptions.filter(
      ({ option }) => groupOf(option) === 'transport',
    ),
    stays: prototypeOptions.filter(({ option }) => groupOf(option) === 'stays'),
  };
  const currentGroupedOptions = {
    transport: currentPrototypeOptions.filter(
      ({ option }) => groupOf(option) === 'transport',
    ),
    stays: currentPrototypeOptions.filter(
      ({ option }) => groupOf(option) === 'stays',
    ),
  };
  const visibleOptions = groupedOptions[activeGroup];
  const activeOptions = groupedOptions[activeGroup].map(({ option }) => option);
  const currency = activeOptions[0]?.currency;
  const uniformCurrency =
    Boolean(currency) &&
    activeOptions.every((option) => option.currency === currency);
  const availableSorts = (
    activeGroup === 'transport' ? TRANSPORT_SORTS : STAY_SORTS
  ).filter(([key]) => key !== 'price' || uniformCurrency);
  const activeSort: SortKey = availableSorts.some(
    ([key]) => key === sorts[activeGroup],
  )
    ? sorts[activeGroup]
    : 'recommended';
  const activeKinds = Array.from(
    new Set(visibleOptions.map(({ option }) => option.kind)),
  );
  if (pinnedOption && groupOf(pinnedOption) === activeGroup) {
    activeKinds.sort(
      (left, right) =>
        Number(right === pinnedOption.kind) -
        Number(left === pinnedOption.kind),
    );
  }
  const assistant = agentName || 'Dream';
  const countdownSeconds = returnRefreshAt
    ? Math.ceil((Date.parse(returnRefreshAt) - countdownNow) / 1000)
    : 0;
  const refreshCountdown =
    countdownSeconds > 0 ? formatCountdown(countdownSeconds) : null;
  const returnCountdown =
    refreshCountdown === null ? null : (
      <p
        className='to-muted'
        style={{ marginTop: 8 }}
        data-testid='return-countdown-page'
      >
        <span aria-hidden='true'>*</span>
        {t('travelOptions.returnRefreshCountdown', {
          time: refreshCountdown,
        })}
      </p>
    );
  const isStaleFlight = (card: TravelCard | null | undefined) =>
    card?.kind === 'flight' &&
    refreshCountdown !== null &&
    hasStaleReturnChoices(card.id, savedReturnRows, countdownNow);

  useEffect(() => {
    setLocalChoices(null);
    setReturnRefreshAt(null);
  }, [artifact.id]);

  useEffect(() => {
    if (!returnRefreshAt || !Number.isFinite(Date.parse(returnRefreshAt))) {
      return undefined;
    }
    const tick = () => {
      const now = Date.now();
      setCountdownNow(now);
      if (Date.parse(returnRefreshAt) <= now) setReturnRefreshAt(null);
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [returnRefreshAt]);

  useEffect(() => {
    if (!userSelectedGroup.current && pinnedOption) {
      setActiveGroup(groupOf(pinnedOption));
      return;
    }
    if (userSelectedGroup.current) return;
    if (groupedOptions[activeGroup].length > 0) return;
    setActiveGroup(groupedOptions.transport.length ? 'transport' : 'stays');
  }, [activeGroup, groupedOptions, pinnedOption]);

  useEffect(() => {
    let cancelled = false;
    const loadRelated = async () => {
      setRelatedLoading(true);
      try {
        const config =
          !shareToken && isAuthenticated
            ? {
                headers: {
                  Authorization: `Bearer ${await getAccessTokenSilently()}`,
                },
              }
            : undefined;
        const response = shareToken
          ? await api.get<RelatedResponse>(
              `/public/artifacts/${encodeURIComponent(shareToken)}/related-travel`,
            )
          : await api.get<RelatedResponse>(
              `/response-artifacts/${encodeURIComponent(artifact.id)}/related-travel`,
              config,
            );
        if (cancelled) return;
        const related = await Promise.all(
          (response.data.artifacts ?? []).map(async (item) => {
            const sourceArtifact = item.artifact;
            let relatedArtifact = sourceArtifact;
            if (sourceArtifact?.status === 'actioned') {
              const flightState = readFlightBookingState(
                sourceArtifact.userResponse,
              );
              const hotelState = readHotelBookingState(
                sourceArtifact.userResponse,
              );
              if (
                flightState?.state === 'not_booked' ||
                hotelState?.state === 'not_booked'
              ) {
                return [];
              }
            }
            if (relatedArtifact && item.shareToken) {
              try {
                if (needsLivePrices(relatedArtifact as ResponseArtifactDTO)) {
                  await refreshLivePrices(item.shareToken);
                  const refreshed = await api.get<{
                    success: boolean;
                    artifact?: MultipleChoiceArtifact;
                  }>(
                    `/public/artifacts/${encodeURIComponent(item.shareToken)}`,
                  );
                  relatedArtifact = refreshed.data.artifact ?? relatedArtifact;
                }
              } catch (error) {
                if (!isLivePricesRequired(error)) return [];
              }
            }
            return relatedArtifact ? adaptTravelChoices(relatedArtifact) : [];
          }),
        );
        if (cancelled) return;
        const firstRelatedTripName = (response.data.artifacts ?? [])
          .map((item) => item.artifact?.tripName)
          .find(
            (name): name is string =>
              typeof name === 'string' && name.trim().length > 0,
          );
        setRelatedTripName(firstRelatedTripName ?? null);
        setRelatedShareTokens(
          Object.fromEntries(
            (response.data.artifacts ?? []).flatMap((item) =>
              item.artifact?.id
                ? [[item.artifact.id, item.shareToken ?? null]]
                : [],
            ),
          ),
        );
        setRelatedArtifactStates(
          Object.fromEntries(
            (response.data.artifacts ?? []).flatMap((item) =>
              item.artifact?.id
                ? [
                    [
                      item.artifact.id,
                      {
                        status: item.artifact.status,
                        userResponse: item.artifact.userResponse,
                      },
                    ],
                  ]
                : [],
            ),
          ),
        );
        setRelatedCards(related.flat());
      } catch {
        if (!cancelled) {
          setRelatedCards([]);
          setRelatedTripName(null);
          setRelatedShareTokens({});
          setRelatedArtifactStates({});
        }
      } finally {
        if (!cancelled) setRelatedLoading(false);
      }
    };
    loadRelated();
    return () => {
      cancelled = true;
    };
  }, [artifact.id, getAccessTokenSilently, isAuthenticated, shareToken]);

  const fetchFlight = useFlightArtifactFetcher(artifact.id, shareToken);
  const flightBookingState = useFlightBookingState(
    artifact.id,
    activeBooking?.card.kind === 'flight',
    fetchFlight,
  );
  const fetchHotel = useHotelArtifactFetcher(artifact.id, shareToken);
  const hotelBookingState = useHotelBookingState(
    artifact.id,
    activeBooking?.card.kind === 'hotel' ? bookingAttempt : null,
    fetchHotel,
  );
  const hotelQuoteRequest =
    pending?.hotel && pending.offerId
      ? {
          artifactId: artifact.id,
          choiceId: pending.card.id,
          offerId: pending.offerId,
          shareToken,
        }
      : null;
  const { state: hotelQuote, refresh: refreshHotelQuote } =
    useHotelRateQuote(hotelQuoteRequest);

  const selectedIdsForCard = (card: TravelCard) =>
    card.artifactId === artifact.id
      ? selectedChoiceIds
      : selectedIdsFromResponse(
          relatedArtifactStates[card.artifactId]?.userResponse,
        );
  const isActionedCard = (card: TravelCard) =>
    card.artifactId === artifact.id
      ? artifact.status === 'actioned'
      : relatedArtifactStates[card.artifactId]?.status === 'actioned';
  const bookingStatusForCard = (card: TravelCard): string | null => {
    if (!isActionedCard(card) || !selectedIdsForCard(card).includes(card.id)) {
      return null;
    }

    if (card.kind === 'flight') {
      const state =
        (card.artifactId === artifact.id
          ? flightBookingState
          : readFlightBookingState(
              relatedArtifactStates[card.artifactId]?.userResponse,
            )) ??
        (card.artifactId === artifact.id
          ? readFlightBookingState(artifact.userResponse)
          : null);
      const option = prototypeOption(card);
      if (!state || !option || option.kind !== 'flight' || !option.legs[0]) {
        return null;
      }
      const leg = option.legs[0];
      const details = {
        time: timeOf(leg.dep),
        place: leg.to,
        date: ukDate(leg.dep),
      };
      switch (state.state) {
        case 'booking':
        case 'awaiting_payment':
          return t('travelOptions.flightBookingPending', details);
        case 'booked':
          if (state.status === 'confirmed') {
            return t('travelOptions.flightBookingConfirmed', {
              ...details,
              reference: state.bookingReference
                ? ` Ref ${state.bookingReference}`
                : '',
            });
          }
          return t('travelOptions.flightBookingPending', details);
        case 'unknown':
          return t('travelOptions.flightBookingUnknown', details);
        case 'not_booked':
          return t('travelOptions.flightBookingNotBooked', details);
        default:
          return null;
      }
    }

    if (card.kind !== 'hotel') return null;
    const state =
      card.artifactId === artifact.id
        ? (hotelBookingState ?? readHotelBookingState(artifact.userResponse))
        : readHotelBookingState(
            relatedArtifactStates[card.artifactId]?.userResponse,
          );
    if (!state) return null;
    const details = { name: card.title };
    switch (state.state) {
      case 'booking':
      case 'awaiting_payment':
        return t('travelOptions.hotelBookingPending', details);
      case 'booked':
        return t('travelOptions.hotelBookingConfirmed', {
          ...details,
          reference: state.hotelConfirmationCode
            ? ` Ref ${state.hotelConfirmationCode}`
            : '',
        });
      case 'unknown':
        return t('travelOptions.hotelBookingUnknown', details);
      case 'not_booked':
        return t('travelOptions.hotelBookingNotBooked', details);
      default:
        return null;
    }
  };
  const doneLabel = (card: TravelCard) =>
    listClosed(card) &&
    selectedIdsForCard(card).includes(card.id) &&
    !bookingStatusForCard(card)
      ? 'Chosen'
      : null;

  const callReturns = useCallback(
    async (card: TravelCard) => {
      if (cardBookingDisabled(card)) return;
      if (card.artifactId !== artifact.id) {
        if (!openRelatedList(card)) setSelected(card);
        return;
      }
      setBookingNotice(null);
      const offer = flightOffer(card.choice);
      if (!offer?.departureTokenId) {
        await onBook(
          { choiceId: card.id },
          desiredAction('find_more', t('travelOptions.seeReturnTimes')),
          [],
        );
        return;
      }
      setSelected(card);
      setReturnsLoading(true);
      setReturnError(null);
      setReturnChoicesById(new Map());
      try {
        const config =
          !shareToken && isAuthenticated
            ? {
                headers: {
                  Authorization: `Bearer ${await getAccessTokenSilently()}`,
                },
              }
            : undefined;
        const response = shareToken
          ? await api.post<ReturnResponse>(
              `/public/artifacts/${encodeURIComponent(shareToken)}/choices/${encodeURIComponent(card.id)}/returns`,
            )
          : await api.post<ReturnResponse>(
              `/response-artifacts/${encodeURIComponent(artifact.id)}/choices/${encodeURIComponent(card.id)}/returns`,
              {},
              config,
            );
        if (response.data.refreshAt) {
          setReturnRefreshAt(response.data.refreshAt);
          setCountdownNow(Date.now());
        }
        const returnCards = adaptTravelChoices(
          {
            id: artifact.id,
            title: artifact.title,
            choices: [
              ...(response.data.sameTicket ?? []),
              ...(response.data.separateTickets ?? []),
            ],
          },
          { includeReturnChoices: true },
        );
        setReturnChoicesById(
          new Map(returnCards.map((returnCard) => [returnCard.id, returnCard])),
        );
      } catch (error) {
        if (isUnavailableReturns(error)) {
          await onBook(
            { choiceId: card.id },
            desiredAction('find_more', t('travelOptions.seeReturnTimes')),
            [],
          );
          return;
        }
        setReturnError(
          isRateLimited(error) ? 'returnsRateLimited' : 'returnsError',
        );
      } finally {
        setReturnsLoading(false);
      }
    },
    [
      artifact.id,
      artifact.title,
      cardBookingDisabled,
      getAccessTokenSilently,
      isAuthenticated,
      onBook,
      openRelatedList,
      shareToken,
      t,
    ],
  );

  const openBook = async (
    card: TravelCard,
    offerId?: string,
    farePosition?: number,
    pairedOutbound?: { card: TravelCard; farePosition?: number },
  ) => {
    if (cardBookingDisabled(card)) return;
    if (card.artifactId !== artifact.id) {
      openRelatedList(card);
      return;
    }
    setBookingNotice(null);
    if (
      card.kind === 'flight' &&
      shareToken &&
      (choiceNeedsLivePrices(card.choice) ||
        (pairedOutbound && choiceNeedsLivePrices(pairedOutbound.card.choice)))
    ) {
      const fareIndex = (
        choice: TravelChoice,
        requestedPosition?: number,
        selectedOfferId?: string,
      ) => {
        const fares = flightOffer(choice)?.fares ?? [];
        if (requestedPosition !== undefined) return requestedPosition;
        const selectedIndex = selectedOfferId
          ? fares.findIndex((fare) => fare.offerId === selectedOfferId)
          : -1;
        return selectedIndex >= 0 ? selectedIndex : 0;
      };
      const selectedFarePosition = fareIndex(
        card.choice,
        farePosition,
        offerId,
      );
      const outboundFarePosition = pairedOutbound
        ? fareIndex(pairedOutbound.card.choice, pairedOutbound.farePosition)
        : undefined;
      setBookingRefreshing(true);
      try {
        await refreshLivePrices(shareToken);
        const response = await api.get<PublicTravelArtifactResponse>(
          `/public/artifacts/${encodeURIComponent(shareToken)}`,
        );
        const refreshedChoices = response.data.artifact?.choices;
        if (!response.data.success || !Array.isArray(refreshedChoices)) {
          throw new Error('Refreshed travel choices were not returned');
        }
        setLocalChoices(refreshedChoices);
        const refreshedArtifact = {
          ...pageArtifact,
          choices: refreshedChoices,
        };
        const refreshedCards = adaptTravelChoices(refreshedArtifact, {
          includeReturnChoices: true,
        });
        const refreshedCard = refreshedCards.find(
          (candidate) =>
            candidate.artifactId === card.artifactId &&
            candidate.id === card.id,
        );
        const refreshedOutbound = pairedOutbound
          ? refreshedCards.find(
              (candidate) =>
                candidate.artifactId === pairedOutbound.card.artifactId &&
                candidate.id === pairedOutbound.card.id,
            )
          : undefined;
        if (!refreshedCard || (pairedOutbound && !refreshedOutbound)) {
          throw new Error('A selected flight choice was not returned');
        }
        const returnFor =
          pairedOutbound?.card.id ??
          (typeof card.choice.returnFor === 'string'
            ? card.choice.returnFor
            : null);
        if (returnFor) {
          setReturnChoicesById((current) => {
            const next = new Map(current);
            next.forEach((returned, id) => {
              if (returned.choice.returnFor === returnFor) next.delete(id);
            });
            refreshedCards.forEach((returned) => {
              if (returned.choice.returnFor === returnFor) {
                next.set(returned.id, returned);
              }
            });
            return next;
          });
        }
        const oldFares = flightOffer(card.choice)?.fares ?? [];
        const refreshedFare = flightOffer(refreshedCard.choice)?.fares?.[
          selectedFarePosition
        ];
        const refreshedOutboundFare = pairedOutbound
          ? flightOffer(refreshedOutbound!.choice)?.fares?.[
              outboundFarePosition ?? 0
            ]
          : undefined;
        const oldOutboundFares = pairedOutbound
          ? (flightOffer(pairedOutbound.card.choice)?.fares ?? [])
          : [];
        if (
          (oldFares.length > 0 && !refreshedFare) ||
          choiceNeedsLivePrices(refreshedCard.choice) ||
          (pairedOutbound &&
            oldOutboundFares.length > 0 &&
            (!refreshedOutboundFare ||
              choiceNeedsLivePrices(refreshedOutbound!.choice)))
        ) {
          throw new Error('The refreshed flight fare is unavailable');
        }
        setPending({
          card: refreshedCard,
          offerId: refreshedFare?.offerId ?? offerId,
          farePosition: selectedFarePosition,
          hotel: false,
        });
        return;
      } catch {
        setBookingNotice(t('travelOptions.flightUnavailable'));
        return;
      } finally {
        setBookingRefreshing(false);
      }
    }
    // Dream rail fares are prepared first; payment is approved on the later card.
    if (areDreamRailChoices(pageArtifact.choices, [card.id])) {
      const storedRailOfferId = card.choice.railOffer?.offerId;
      const selectedOfferId =
        offerId ??
        (typeof storedRailOfferId === 'string' ? storedRailOfferId : undefined);
      void onBook(
        {
          choiceId: card.id,
          ...(selectedOfferId ? { selectedOfferId } : {}),
        },
        desiredAction('buy', t('travelOptions.book')),
        [],
      );
      return;
    }
    setPending({
      card,
      offerId,
      farePosition,
      hotel: card.kind === 'hotel' && Boolean(offerId),
    });
  };

  const pendingOffer = pending ? flightOffer(pending.card.choice) : undefined;
  const pendingOption = pending ? prototypeOption(pending.card) : null;
  const pendingApproval = pendingOption
    ? travelApprovalDetails(pendingOption, {
        offerId: pending?.offerId,
        farePosition: pending?.farePosition,
        ...(pending?.hotel && hotelQuote.status === 'quoted'
          ? { quote: hotelQuote.quote }
          : {}),
        separateTickets: pendingOffer?.separateTickets,
      })
    : null;
  const pendingItems: ReplyToItem[] = pending
    ? [
        {
          id: pending.offerId ?? pending.card.id,
          actionLine: t('travelOptions.book'),
          descriptionLine: pending.card.title,
          bookable: true,
          price: pendingApproval?.price ?? pending.card.price ?? 0,
          price_currency:
            pendingApproval?.currency ?? pending.card.currency ?? 'GBP',
        },
      ]
    : [];

  const approveBooking = async (cardSelection?: CardSelection) => {
    if (!pending) return;
    const selection: TravelSelection =
      pending.card.kind === 'hotel'
        ? {
            choiceId: pending.card.id,
            offerId: pending.offerId,
            ...(hotelQuote.status === 'quoted'
              ? { quoteId: hotelQuote.quote.quoteId }
              : {}),
          }
        : { choiceId: pending.card.id, offerId: pending.offerId };
    setActiveBooking(pending);
    setBookingAttempt((attempt) => attempt + 1);
    try {
      await onBook(
        selection,
        desiredAction('buy', t('travelOptions.book')),
        [],
        cardSelection,
      );
    } finally {
      setPending(null);
    }
  };

  const tabs: Array<{ group: 'transport' | 'stays'; label: string }> = [
    { group: 'transport', label: t('travelOptions.flights') },
    { group: 'stays', label: t('travelOptions.hotels') },
  ];
  const availableTabs = tabs.filter(
    ({ group }) => groupedOptions[group].length > 0,
  );
  const renderedTabs =
    availableTabs.length > 0
      ? availableTabs
      : [tabs.find(({ group }) => group === activeGroup) ?? tabs[0]];
  const hasTabs = renderedTabs.length > 1;
  const currentVisibleOptions = currentGroupedOptions[activeGroup];
  const pendingRelatedSkeletons = relatedLoading
    ? Array.from({ length: 3 }, (_, index) => {
        const option =
          currentVisibleOptions[
            index % Math.max(currentVisibleOptions.length, 1)
          ]?.option;
        return {
          kind: option?.kind ?? (activeGroup === 'stays' ? 'hotel' : 'flight'),
          paired: option?.kind === 'flight' && option.legs.length > 1,
        };
      })
    : [];
  const hotelMapChoices = cards
    .filter((card) => card.kind === 'hotel')
    .map(({ choice }) => ({
      choiceId: choice.choiceId,
      choiceTitle: choice.choiceTitle ?? '',
      choiceBlurb:
        typeof choice.choiceBlurb === 'string' ? choice.choiceBlurb : '',
      lat: typeof choice.lat === 'number' ? choice.lat : undefined,
      lng: typeof choice.lng === 'number' ? choice.lng : undefined,
    }))
    .filter(
      (choice) =>
        typeof choice.lat === 'number' && typeof choice.lng === 'number',
    );
  const activeFlight = activeBooking
    ? flightOffer(activeBooking.card.choice)
    : undefined;
  const activeHotel = activeBooking
    ? hotelOffer(activeBooking.card.choice)
    : undefined;
  const activeRoom = activeHotel?.rooms?.[0];
  const activeRate = activeRoom?.rates?.find(
    (rate) => rate.offerId === activeBooking?.offerId,
  );
  const returnOptions = returnCards
    .map((card) => adaptReturnChoice(card.artifactId, card.choice as never))
    .filter((option): option is ReturnFlight => option !== null);
  const sameTicketOptions = returnOptions.filter(
    (option) => !option.separateTickets,
  );
  const separateTicketOptions = returnOptions.filter(
    (option) => option.separateTickets,
  );
  const busy = shouldShowTravelSkeletons(relatedLoading, returnsLoading);

  return (
    <section className='travel-options' aria-label={t('travelOptions.title')}>
      {pinnedChoice && (
        <section className='to-pick'>
          <h2 className='to-eyebrow'>Your pick</h2>
          {!pinnedOption ? (
            <SkeletonCard />
          ) : (
            <OptionCard
              option={pinnedOption}
              stale={isStaleFlight(pinnedCard)}
              picked
              bookingDisabled={cardBookingDisabled(pinnedCard)}
              done={pinnedCard ? doneLabel(pinnedCard) : null}
              bookingStatus={
                pinnedCard ? bookingStatusForCard(pinnedCard) : null
              }
              onOpen={() => {
                const card = pinnedCard;
                if (card?.kind === 'flight') {
                  if (
                    pinnedOption?.kind === 'flight' &&
                    pinnedOption.mode === 'select' &&
                    !cardBookingDisabled(card)
                  ) {
                    void callReturns(card);
                  } else {
                    setSelected(card);
                  }
                } else if (card) setSelected(card);
              }}
              onBook={() => {
                const card = pinnedCard;
                if (!card) return;
                if (cardBookingDisabled(card)) return;
                if (
                  card.kind === 'flight' &&
                  pinnedOption?.kind === 'flight' &&
                  pinnedOption.mode === 'select'
                ) {
                  void callReturns(card);
                } else {
                  openBook(card);
                }
              }}
            />
          )}
          {returnCountdown}
        </section>
      )}

      {!pinnedChoice && returnCountdown}

      <section className='to-all'>
        <div className='to-bar to-bar--inline'>
          {pinnedChoice && <h2 className='to-eyebrow'>All options</h2>}
          {activeOptions.length > 1 && (
            <label className='to-sort'>
              <span>{t('travelOptions.sort.label')}</span>
              <select
                aria-label={t('travelOptions.sort.label')}
                value={activeSort}
                onChange={(event) =>
                  setSorts((current) => ({
                    ...current,
                    [activeGroup]: event.target.value as SortKey,
                  }))
                }
              >
                {availableSorts.map(([key, label]) => (
                  <option key={key} value={key}>
                    {t(label)}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        {hasTabs && (
          <div className='to-segs' role='tablist'>
            {renderedTabs.map(({ group, label }) => (
              <button
                key={group}
                type='button'
                role='tab'
                aria-selected={activeGroup === group}
                className={`to-seg${activeGroup === group ? ' is-on' : ''}`}
                onClick={() => {
                  userSelectedGroup.current = true;
                  setActiveGroup(group);
                }}
              >
                {group === 'transport' ? 'Flights & trains' : label}
                <span className='to-seg__n'>
                  {groupedOptions[group].length}
                </span>
              </button>
            ))}
          </div>
        )}
        {activeKinds.length === 0 && relatedLoading && (
          <div className='to-list'>
            {pendingRelatedSkeletons.map(({ kind, paired }, index) => (
              <SkeletonCard
                key={`related-loading-${index}`}
                kind={kind}
                paired={paired}
              />
            ))}
          </div>
        )}
        {activeKinds.map((kind) => {
          const entries = visibleOptions.filter(
            ({ option }) => option.kind === kind,
          );
          const entriesByOption = new Map(
            entries.map((entry) => [entry.option, entry]),
          );
          const sortedEntries = sorted(
            entries.map(({ option }) => option),
            activeSort,
          ).map((option) => entriesByOption.get(option)!);
          const title =
            kind === 'flight'
              ? 'Flights'
              : kind === 'hotel'
                ? 'Stays'
                : 'Trains';
          const loadingForKind = pendingRelatedSkeletons.filter(
            (placeholder) => placeholder.kind === kind,
          );
          return (
            <section className='to-sec' key={kind}>
              <div className='to-sec__head'>
                <h2 className={`to-sec__title to-sec__title--${kind}`}>
                  {title}
                  <span className='to-seg__n'>{entries.length}</span>
                </h2>
              </div>
              <div className='to-list'>
                {sortedEntries.map(({ card, option }) => (
                  <OptionCard
                    key={`${card.artifactId}-${card.id}`}
                    option={option}
                    stale={isStaleFlight(card)}
                    bookingDisabled={cardBookingDisabled(card)}
                    done={doneLabel(card)}
                    bookingStatus={bookingStatusForCard(card)}
                    onOpen={() => {
                      if (card.kind === 'flight') {
                        if (
                          option.mode === 'select' &&
                          !cardBookingDisabled(card)
                        ) {
                          void callReturns(card);
                        } else {
                          setSelected(card);
                        }
                      } else {
                        setSelected(card);
                      }
                    }}
                    onBook={() => {
                      if (cardBookingDisabled(card)) return;
                      if (card.kind === 'flight' && option.mode === 'select') {
                        void callReturns(card);
                      } else {
                        openBook(card);
                      }
                    }}
                  />
                ))}
                {loadingForKind.map(({ paired }, index) => (
                  <SkeletonCard
                    key={`related-loading-${kind}-${index}`}
                    kind={kind}
                    paired={paired}
                  />
                ))}
                {kind === 'hotel' && hotelMapChoices.length > 0 && (
                  <HotelArtifactMap
                    choices={hotelMapChoices}
                    activeId={
                      pinnedOption?.kind === 'hotel'
                        ? pinnedOption.id
                        : undefined
                    }
                    onSelect={(choiceId) => {
                      const hotel = cards.find((card) => card.id === choiceId);
                      if (hotel) setSelected(hotel);
                    }}
                  />
                )}
              </div>
            </section>
          );
        })}
      </section>

      {selected?.kind === 'flight' &&
        (() => {
          const selectedChoiceId = selected.id;
          const selectedOption = prototypeOptions.find(
            ({ card }) => card.id === selected.id,
          )?.option;
          if (!selectedOption || selectedOption.kind === 'hotel') return null;
          return (
            <>
              <FlightSheet
                flight={selectedOption}
                sameTicket={sameTicketOptions}
                separateTickets={separateTicketOptions}
                onClose={() => {
                  setSelected(null);
                  setReturnChoicesById(new Map());
                  setReturnError(null);
                  setBookingNotice(null);
                }}
                onBookOne={(option, index) => {
                  const card = cards.find((item) => item.id === option.id);
                  const fare = option.fares[index];
                  if (card) void openBook(card, fare?.id, index);
                }}
                onOtherReturns={() => {
                  if (cardBookingDisabled(selected)) return;
                  if (selected.artifactId !== artifact.id) {
                    openRelatedList(selected);
                    return;
                  }
                  void onBook(
                    { choiceId: selectedChoiceId },
                    desiredAction(
                      'find_more',
                      `Other return times (${selectedOption.returnAlternatives})`,
                    ),
                    [],
                  );
                }}
                onBookPair={(_option, returned, fares, positions) => {
                  const card = returned.choiceId
                    ? returnChoicesById.get(returned.choiceId)
                    : undefined;
                  const offer = card ? flightOffer(card.choice) : undefined;
                  const offerId =
                    offer?.bookingMode === 'instant' ? fares[1].id : undefined;
                  if (card && selected) {
                    void openBook(card, offerId, positions[1], {
                      card: selected,
                      farePosition: positions[0],
                    });
                  }
                }}
                returnsLoading={returnsLoading}
                returnsNotice={
                  returnError === 'returnsRateLimited'
                    ? t('travelOptions.returnsRateLimited')
                    : null
                }
                returnsFailure={
                  returnError === 'returnsError'
                    ? {
                        title: t('travelOptions.returnsErrorTitle'),
                        body: t('travelOptions.returnsErrorBody'),
                      }
                    : null
                }
                returnRefreshNotice={
                  refreshCountdown
                    ? t('travelOptions.returnRefreshCountdown', {
                        time: refreshCountdown,
                      })
                    : null
                }
                onRetryReturns={() => {
                  if (selected) void callReturns(selected);
                }}
                tryAgainLabel={t('travelOptions.tryAgain')}
                backLabel={t('travelOptions.back')}
                bookingDisabled={cardBookingDisabled(selected)}
                bookingRefreshing={bookingRefreshing}
                bookingNotice={bookingNotice}
                gettingLivePricesLabel={t(
                  'travelOptions.gettingLivePricesButton',
                )}
              />
            </>
          );
        })()}

      {selected && selected.kind !== 'flight' && (
        <DetailsSheet
          option={
            prototypeOptions.find(({ card }) => card.id === selected.id)
              ?.option ?? null
          }
          onClose={() => setSelected(null)}
          onBook={(option, index) => {
            const card = cards.find((item) => item.id === option.id);
            const rate = option.kind === 'hotel' ? option.rates[index] : null;
            const fare = option.kind === 'hotel' ? null : option.fares[index];
            if (card) openBook(card, rate?.id ?? fare?.id, index);
          }}
          bookingDisabled={cardBookingDisabled(selected)}
        />
      )}

      <SpendingApprovalModal
        show={pending != null}
        onApprove={approveBooking}
        onCancel={() => setPending(null)}
        items={pendingItems}
        agentImageUrl={agentImageUrl}
        assistantName={assistant}
        exactAmount
        structuredDetails={pendingApproval?.details}
        shareToken={shareToken}
        checkoutResume={artifactCheckoutResume(artifact.id)}
        title={t('travelOptions.confirmBooking')}
        approveLabel={t('travelOptions.book')}
        {...(pending?.hotel
          ? {
              details: (
                <HotelRateQuoteNotice
                  state={hotelQuote}
                  assistantName={assistant}
                  onRetry={refreshHotelQuote}
                  onPickAnother={() => setPending(null)}
                />
              ),
              approveDisabled: hotelQuote.status !== 'quoted',
            }
          : {})}
      />
      {activeBooking?.card.kind === 'flight' && activeFlight && (
        <FlightBookingProgressSheet
          show
          state={
            flightBookingState ?? readFlightBookingState(artifact.userResponse)
          }
          summary={{
            route: `${activeFlight.slices[0]?.origin ?? ''} → ${activeFlight.slices[0]?.destination ?? ''}`,
            airline:
              activeFlight.slices[0]?.segments
                .map((segment) => segment.carrierName)
                .join(', ') ?? '',
            fareBrand: activeBooking.card.title,
            passengers: Array.from(
              { length: activeFlight.passengerCount || 1 },
              (_, index) => `${t('travelOptions.traveller')} ${index + 1}`,
            ),
            total: activeBooking.card.price ?? 0,
            currency: activeBooking.card.currency ?? 'GBP',
          }}
          assistantName={assistant}
          notifyVia={shareToken ? 'whatsapp' : 'chat'}
          onDone={() => setActiveBooking(null)}
        />
      )}
      {activeBooking?.card.kind === 'hotel' && activeHotel && (
        <HotelBookingProgressSheet
          show
          state={
            hotelBookingState ?? readHotelBookingState(artifact.userResponse)
          }
          summary={{
            hotelName: activeBooking.card.title,
            room: activeRoom?.name ?? '',
            board: activeRate?.board ?? '',
            total: activeRate?.total ?? activeBooking.card.price ?? 0,
            currency:
              activeRate?.currency ?? activeBooking.card.currency ?? 'GBP',
          }}
          assistantName={assistant}
          notifyVia={shareToken ? 'whatsapp' : 'chat'}
          onDone={() => setActiveBooking(null)}
        />
      )}
      {onChatAbout && (
        <button
          type='button'
          className='travel-options-chat'
          onClick={onChatAbout}
        >
          {chatButtonText}
        </button>
      )}
      <span className='visually-hidden' aria-live='polite'>
        {busy ? t('travelOptions.loading') : ''}
      </span>
    </section>
  );
};

export default TravelOptionsPage;
